declare const lngLatBrand: unique symbol;

export type LngLat = readonly [longitude: number, latitude: number] & {
  readonly [lngLatBrand]: true;
};

export interface CameraState {
  readonly center: LngLat;
  readonly zoom: number;
  readonly bearing: number;
  readonly pitch: number;
}

export interface GeoBounds {
  readonly southWest: LngLat;
  readonly northEast: LngLat;
}

function requireFinite(value: number, name: string): number {
  if (!Number.isFinite(value)) {
    throw new RangeError(`${name} must be finite`);
  }
  return value;
}

function requireRange(value: number, minimum: number, maximum: number, name: string): number {
  requireFinite(value, name);
  if (value < minimum || value > maximum) {
    throw new RangeError(`${name} must be between ${String(minimum)} and ${String(maximum)}`);
  }
  return value;
}

export function createLngLat(longitude: number, latitude: number): LngLat {
  requireRange(longitude, -180, 180, 'longitude');
  requireRange(latitude, -90, 90, 'latitude');
  return Object.freeze([longitude, latitude]) as LngLat;
}

export function normalizeBearing(bearing: number): number {
  requireFinite(bearing, 'bearing');
  const normalized = ((((bearing + 180) % 360) + 360) % 360) - 180;
  return Object.is(normalized, -0) ? 0 : normalized;
}

export function createCameraState(input: {
  readonly center: LngLat;
  readonly zoom: number;
  readonly bearing: number;
  readonly pitch: number;
}): CameraState {
  return Object.freeze({
    center: input.center,
    zoom: requireRange(input.zoom, 0, 22, 'zoom'),
    bearing: normalizeBearing(input.bearing),
    pitch: requireRange(input.pitch, 0, 60, 'pitch'),
  });
}

export function createGeoBounds(southWest: LngLat, northEast: LngLat): GeoBounds {
  if (southWest[0] > northEast[0] || southWest[1] > northEast[1]) {
    throw new RangeError('bounds must run from south-west to north-east');
  }
  return Object.freeze({ southWest, northEast });
}

export function containsCoordinate(bounds: GeoBounds, coordinate: LngLat): boolean {
  return (
    coordinate[0] >= bounds.southWest[0] &&
    coordinate[0] <= bounds.northEast[0] &&
    coordinate[1] >= bounds.southWest[1] &&
    coordinate[1] <= bounds.northEast[1]
  );
}
